/**
 * The pure part of the relay, shared with the game: the room codes, the origins the relay serves,
 * the seat a newcomer gets, and the two message shapes on the wire. The Durable Object in
 * `index.ts` is the glue around these; the game imports this module for the codes and the close
 * codes (`packages/app/src/online`).
 *
 * On the wire a room's socket carries text only. A notice from the relay itself starts with `@`
 * and is JSON (`@{"t":"welcome",...}`); a message from another player starts with that player's
 * peer number and a colon (`2:["i",...]`). The relay sanitizes hello names and preserves other messages.
 */

/** The characters of a room code: no 0, O, 1, or I, so a code read out loud or typed is never ambiguous. */
export const CODE_ALPHABET = "23456789ABCDEFGHJKLMNPQRSTUVWXYZ";
export const CODE_LENGTH = 4;
/** Players a room holds: the host and up to three guests, the split-screen race's four. */
export const MAX_PEERS = 4;

/** WebSocket close codes the relay uses (4000 to 4999 are free for applications). */
export const CLOSE = {
  /** A second host asked for a code that already has one. */
  ROOM_TAKEN: 4001,
  /** A guest asked for a code nobody hosts. */
  NO_ROOM: 4002,
  /** Every seat is taken. */
  ROOM_FULL: 4003,
  /** The host explicitly left: the room is over. */
  HOST_LEFT: 4004,
  /** The request made no sense (a bad code, a bad role). */
  BAD_REQUEST: 4005,
  /** A returning player no longer owns a reserved seat in this room. */
  BAD_RESUME: 4006,
  /** This connection was replaced by the same player's newer connection. */
  REPLACED: 4007,
} as const;

export const CLOSE_REASONS: Record<number, string> = {
  [CLOSE.ROOM_TAKEN]: "that code is taken",
  [CLOSE.NO_ROOM]: "no race with that code",
  [CLOSE.ROOM_FULL]: "that race is full",
  [CLOSE.HOST_LEFT]: "the host left",
  [CLOSE.BAD_REQUEST]: "bad request",
  [CLOSE.BAD_RESUME]: "your place in that room expired",
  [CLOSE.REPLACED]: "connected on another socket",
};

/** A notice from the relay (`@...` on the wire). */
export type RoomNotice =
  | {
      t: "welcome";
      peer: number;
      peers: number[];
      code: string;
      resumeToken?: string;
      resumed?: boolean;
      away?: number[];
    }
  | { t: "join"; peer: number; resumed?: boolean }
  | { t: "away"; peer: number }
  | { t: "leave"; peer: number }
  | { t: "error"; code: number; reason: string };

/** Keep only the alphabet, upper case, so a code pasted with spaces or typed in lower case still works. */
export function cleanCode(raw: string): string {
  let out = "";
  for (const ch of raw.toUpperCase()) if (CODE_ALPHABET.includes(ch)) out += ch;
  return out.slice(0, CODE_LENGTH);
}

export function validCode(code: string): boolean {
  return code.length === CODE_LENGTH && cleanCode(code) === code;
}

/** A random code from `random`, a source of numbers in [0, 1) (Math.random, or crypto through a wrapper). */
export function randomCode(random: () => number = Math.random): string {
  let out = "";
  for (let i = 0; i < CODE_LENGTH; i++) {
    const k = Math.min(CODE_ALPHABET.length - 1, Math.floor(random() * CODE_ALPHABET.length));
    out += CODE_ALPHABET[k];
  }
  return out;
}

/**
 * Whether a browser at `origin` may open a room: the game's own addresses, and a developer's machine.
 * A request without an origin (a script, a test) is allowed: the relay holds nothing secret.
 */
export function originAllowed(origin: string | null): boolean {
  if (origin === null || origin === "") return true;
  let host: string;
  try {
    host = new URL(origin).hostname;
  } catch {
    return false;
  }
  if (host === "lache.marshallbruner.com") return true;
  if (host === "lache.pages.dev" || host.endsWith(".lache.pages.dev")) return true;
  return host === "localhost" || host === "127.0.0.1";
}

export type Role = "host" | "guest";

/**
 * The seat a newcomer gets: the host is peer 0 and there is one; guests take the lowest free number
 * from 1 up while the room has a host and a seat. `taken` lists the peers already in the room.
 */
export function assignPeer(role: Role, taken: readonly number[]): { peer: number } | { close: number } {
  const hasHost = taken.includes(0);
  if (role === "host") return hasHost ? { close: CLOSE.ROOM_TAKEN } : { peer: 0 };
  if (!hasHost) return { close: CLOSE.NO_ROOM };
  for (let p = 1; p < MAX_PEERS; p++) if (!taken.includes(p)) return { peer: p };
  return { close: CLOSE.ROOM_FULL };
}

export function encodeNotice(n: RoomNotice): string {
  return `@${JSON.stringify(n)}`;
}

/** A player's message as the others receive it. */
export function encodeRelayed(from: number, text: string): string {
  return `${from}:${text}`;
}

/** The two shapes a room's socket delivers, or null for anything else. */
export function decodeWire(
  text: string,
): { kind: "notice"; notice: RoomNotice } | { kind: "message"; from: number; text: string } | null {
  if (text.startsWith("@")) {
    try {
      const n = JSON.parse(text.slice(1)) as RoomNotice;
      return typeof n === "object" && n !== null && typeof n.t === "string"
        ? { kind: "notice", notice: n }
        : null;
    } catch {
      return null;
    }
  }
  const colon = text.indexOf(":");
  if (colon <= 0) return null;
  const from = Number(text.slice(0, colon));
  if (!Number.isInteger(from) || from < 0 || from >= MAX_PEERS) return null;
  return { kind: "message", from, text: text.slice(colon + 1) };
}

/** Messages longer than this are dropped by the relay: a run's whole tape is a few kilobytes. */
export const MAX_MESSAGE_BYTES = 65536;

/** A disconnected guest's seat, or an empty room, stays available for five minutes. */
export const RECONNECT_GRACE_MS = 5 * 60_000;
/** Negotiated by clients that support private reconnect credentials. */
export const ROOM_PROTOCOL = "lache-v2";
